import type { IGame, InputAction, GameState } from "../../core/types";
import { clamp } from "../../core/Time";

type Bubble = {
  id: number;
  x: number;
  y: number;
  r: number;
  ttl: number;      // time-to-live (secondes)
  alive: boolean;
};

export class PopGame implements IGame {
  private state: GameState = { status: "ready" };

  private viewportW = 0;
  private viewportH = 0;

  private score = 0;
  private combo = 0;
  private t = 0;

  private bubbles: Bubble[] = [];
  private nextId = 1;

  private spawnT = 0;

  reset(): void {
    this.state = { status: "ready" };
    this.score = 0;
    this.combo = 0;
    this.t = 0;
    this.bubbles = [];
    this.spawnT = 0;
    this.nextId = 1;
  }

  getState(): GameState {
    if (this.state.status === "running") return { status: "running", score: this.score };
    if (this.state.status === "gameover") return this.state;
    return { status: "ready" };
  }

  onAction(action: InputAction): void {
    const isStart =
      action.type === "tap" ||
      (action.type === "keydown" && action.key === " ");

    if (this.state.status === "ready" && isStart) {
      this.start();
      return;
    }

    if (this.state.status === "gameover" && isStart) {
      this.reset();
      this.start();
      return;
    }

    if (this.state.status !== "running") return;

    if (action.type === "tap") {
      this.tryPop(action.x, action.y);
      return;
    }

    // option souris: si tu veux cliquer en desktop (pointerdown => tap déjà géré)
    if (action.type === "keydown" && action.key === "Escape") {
      this.state = { status: "gameover", score: this.score, best: 0 };
    }
  }

  private start() {
    this.state = { status: "running", score: 0 };
    this.score = 0;
    this.combo = 0;
    this.t = 0;
    this.bubbles = [];
    this.spawnT = 0;
  }

  update(dt: number): void {
    if (this.state.status !== "running") return;
    if (this.viewportW <= 0 || this.viewportH <= 0) return;

    this.t += dt;

    // spawn plus vite avec le temps
    this.spawnT -= dt;
    const every = clamp(0.85 - this.t * 0.02, 0.20, 0.85);
    if (this.spawnT <= 0) {
      this.spawnT = every;
      this.spawnBubble();
    }

    // update TTL, expire bubbles
    let missed = 0;

    for (const b of this.bubbles) {
      if (!b.alive) continue;
      b.ttl -= dt;
      if (b.ttl <= 0) {
        b.alive = false;
        missed++;
      }
    }

    if (missed > 0) {
      // reset combo si au moins une bulle expirée
      this.combo = 0;
    }

    // nettoyage
    this.bubbles = this.bubbles.filter((b) => b.alive);

    // condition de fin simple : trop de bulles à l’écran
    if (this.bubbles.length >= 12) {
      this.state = { status: "gameover", score: this.score, best: 0 };
    }
  }

  render(ctx: CanvasRenderingContext2D): void {
    const w = ctx.canvas.width;
    const h = ctx.canvas.height;
    this.viewportW = w;
    this.viewportH = h;

    ctx.clearRect(0, 0, w, h);

    // background
    ctx.fillStyle = "rgba(255,255,255,0.03)";
    ctx.fillRect(0, 0, w, h);

    // bubbles
    for (const b of this.bubbles) {
      const life = clamp(b.ttl / 1.2, 0, 1); // indicateur
      ctx.fillStyle = `rgba(59,130,246,${0.25 + 0.35 * life})`;
      ctx.beginPath();
      ctx.arc(b.x, b.y, b.r, 0, Math.PI * 2);
      ctx.fill();

      ctx.strokeStyle = "rgba(255,255,255,0.18)";
      ctx.lineWidth = 2;
      ctx.beginPath();
      ctx.arc(b.x, b.y, b.r, 0, Math.PI * 2);
      ctx.stroke();

      // anneau TTL
      ctx.strokeStyle = "rgba(34,197,94,0.6)";
      ctx.lineWidth = 3;
      ctx.beginPath();
      ctx.arc(b.x, b.y, b.r + 6, -Math.PI / 2, -Math.PI / 2 + Math.PI * 2 * life);
      ctx.stroke();
    }

    // overlay texte
    ctx.fillStyle = "rgba(229,231,235,0.85)";
    ctx.font = "14px system-ui";

    if (this.state.status === "ready") {
      ctx.fillText("Clique/tap pour démarrer — ensuite pop les bulles", 24, h - 24);
    } else if (this.state.status === "gameover") {
      ctx.fillText("Game Over — Espace / clic pour rejouer", 24, h - 24);
    }

    // score
    ctx.fillText(`Combo: ${this.combo}`, 24, 32);
  }

  private spawnBubble() {
    const pad = 40;
    const r = 18 + Math.random() * 18;

    const x = pad + r + Math.random() * (this.viewportW - 2 * (pad + r));
    const y = pad + r + Math.random() * (this.viewportH - 2 * (pad + r));

    // TTL diminue avec le temps (difficulté)
    const ttl = clamp(1.2 - this.t * 0.01, 0.45, 1.2);

    this.bubbles.push({
      id: this.nextId++,
      x,
      y,
      r,
      ttl,
      alive: true
    });
  }

  private tryPop(x: number, y: number) {
    // chercher la bulle la plus proche touchée
    let hitIndex = -1;
    let bestD2 = Infinity;

    for (let i = 0; i < this.bubbles.length; i++) {
      const b = this.bubbles[i];
      const dx = x - b.x;
      const dy = y - b.y;
      const d2 = dx * dx + dy * dy;
      if (d2 <= b.r * b.r && d2 < bestD2) {
        bestD2 = d2;
        hitIndex = i;
      }
    }

    if (hitIndex >= 0) {
      const b = this.bubbles[hitIndex];
      b.alive = false;

      this.combo = clamp(this.combo + 1, 0, 999);
      const points = 10 + this.combo * 2;
      this.score += points;
    } else {
      // click dans le vide = combo reset (option)
      this.combo = 0;
    }
  }
}
